import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { saveConsentSettings, getConsentSettings, syncAnalyticsConsent } from "./analytics";

export const CookieConsentBanner = () => {
  const initialConsent = getConsentSettings();
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(initialConsent ? Boolean(initialConsent.preferences) : true);
  const [marketing, setMarketing] = useState(initialConsent ? Boolean(initialConsent.marketing) : true);
  const [analytics, setAnalytics] = useState(initialConsent ? Boolean(initialConsent.analytics) : true);

  useEffect(() => {
    if (!initialConsent) {
      syncAnalyticsConsent(false);
      return;
    }
    syncAnalyticsConsent(Boolean(initialConsent.analytics));
  }, [initialConsent]);

  useEffect(() => {
    const openPreferences = () => setIsPreferencesOpen(true);
    window.addEventListener("open-cookie-preferences", openPreferences);
    return () => window.removeEventListener("open-cookie-preferences", openPreferences);
  }, []);

  const applyConsent = (value) => {
    saveConsentSettings(value);
    syncAnalyticsConsent(Boolean(value.analytics));
    setPreferences(Boolean(value.preferences));
    setMarketing(Boolean(value.marketing));
    setAnalytics(Boolean(value.analytics));
    setIsPreferencesOpen(false);
  };

  if (!initialConsent && !isPreferencesOpen) {
    return (
      <div data-testid="cookie-banner" className="cookie-banner-shell cookie-banner-shell--compact">
        <Card className="cookie-banner-card cookie-banner-card--compact">
          <CardContent className="cookie-banner-content cookie-banner-content--compact">
            <div data-testid="cookie-banner-compact-copy" className="cookie-banner-compact-copy">
              <strong>Per noi la tua privacy è importante</strong>
              <p>Noi e i nostri partner utilizziamo i cookie e altre tecnologie per migliorare la tua esperienza, misurare le prestazioni e adattare i contenuti. Altre informazioni nella nostra Informativa sulla privacy.</p>
            </div>
            <div data-testid="cookie-banner-compact-actions" className="cookie-banner-actions cookie-banner-actions--compact">
              <button data-testid="cookie-banner-manage" type="button" className="cookie-settings-link cookie-settings-link--inline" onClick={() => setIsPreferencesOpen(true)}>Gestisci preferenze</button>
              <Button data-testid="cookie-banner-compact-accept" type="button" className="btn-emergency" onClick={() => applyConsent({ necessary: true, preferences: true, marketing: true, analytics: true })}>Accetta</Button>
              <Button data-testid="cookie-banner-compact-reject" type="button" className="btn-ghost-tech" onClick={() => applyConsent({ necessary: true, preferences: false, marketing: false, analytics: false })}>Rifiuta</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPreferencesOpen) return null;

  return (
    <div data-testid="cookie-banner" className="cookie-banner-shell">
      <Card className="cookie-banner-card">
        <CardHeader>
          <CardTitle data-testid="cookie-banner-title">Preferenze cookie</CardTitle>
          <CardDescription data-testid="cookie-banner-description">Controllo dei dati</CardDescription>
        </CardHeader>
        <CardContent className="cookie-banner-content">
          <div data-testid="cookie-banner-intro" className="cookie-banner-intro">
            <p>Puoi controllare come vengono utilizzati i tuoi dati sul nostro sito web. Scopri di più sui cookie che utilizziamo qui di seguito e scegli i cookie che fanno per te.</p>
            <div className="cookie-banner-intro__benefits">
              <strong>Se accetti tutti i cookie</strong>
              <ul>
                <li>Il nostro sito potrebbe essere migliorato in base alle tue preferenze</li>
                <li>Le tue preferenze potrebbero essere memorizzate per il tuo ritorno</li>
                <li>Potresti visualizzare contenuti e comunicazioni più pertinenti per te</li>
              </ul>
            </div>
          </div>
          <div data-testid="cookie-banner-categories" className="cookie-banner-categories">
            <label className="cookie-banner-option">
              <input data-testid="cookie-banner-necessary" type="checkbox" checked disabled />
              <span><strong>Funzionamento del sito web</strong><small>Questi cookie sono necessari per il funzionamento del sito e per le attività essenziali come la protezione dalle frodi.</small></span>
            </label>
            <label className="cookie-banner-option">
              <input data-testid="cookie-banner-preferences" type="checkbox" checked={preferences} onChange={(event) => setPreferences(event.target.checked)} />
              <span><strong>Salvataggio delle preferenze</strong><small>Questi cookie consentono di ricordare le tue preferenze, ad esempio il paese e la lingua, e personalizzare la visita successiva al sito web.</small></span>
            </label>
            <label className="cookie-banner-option">
              <input data-testid="cookie-banner-marketing" type="checkbox" checked={marketing} onChange={(event) => setMarketing(event.target.checked)} />
              <span><strong>Personalizza marketing</strong><small>Questi cookie consentono di mostrare annunci su altri siti e comunicazioni di marketing basate sui tuoi interessi.</small></span>
            </label>
            <label className="cookie-banner-option">
              <input data-testid="cookie-banner-analytics" type="checkbox" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} />
              <span><strong>Misurazione delle prestazioni</strong><small>Questi cookie consentono di comprendere il modo in cui interagisci con il sito e il tipo di contenuti più rilevanti.</small></span>
            </label>
          </div>
          <div data-testid="cookie-banner-actions" className="cookie-banner-actions">
            <Button data-testid="cookie-banner-accept" type="button" className="btn-emergency" onClick={() => applyConsent({ necessary: true, preferences: true, marketing: true, analytics: true })}>Accetta tutto</Button>
            <Button data-testid="cookie-banner-reject" type="button" className="btn-ghost-tech" onClick={() => applyConsent({ necessary: true, preferences: false, marketing: false, analytics: false })}>Rifiuta tutto</Button>
            <Button data-testid="cookie-banner-save" type="button" className="btn-whatsapp" onClick={() => applyConsent({ necessary: true, preferences, marketing, analytics })}>Salva le mie scelte</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const CookieSettingsLink = () => (
  <button
    data-testid="cookie-settings-link"
    type="button"
    className="cookie-settings-link"
    onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
  >
    Preferenze cookie
  </button>
);

export const PrivacyPolicyPage = () => (
  <main data-testid="privacy-policy-page" className="legal-page">
    <div className="legal-page__shell">
      <span className="section-eyebrow">Privacy Policy</span>
      <h1>Informativa privacy Viscar</h1>
      <p>Questa è una bozza tecnica iniziale predisposta per il sito Viscar, da validare poi con il consulente legale/privacy dell’azienda.</p>
      <section>
        <h2>Titolare del trattamento</h2>
        <p>Viscar tratta i dati raccolti tramite il sito per finalità di contatto, gestione richieste, misurazione del traffico, analisi delle interazioni e miglioramento dei servizi digitali collegati all’azienda.</p>
      </section>
      <section>
        <h2>Dati raccolti</h2>
        <p>Il sito può raccogliere dati di navigazione, pagine visitate, click su telefono e WhatsApp, richieste inviate tramite i form, dati tecnici di utilizzo, referrer/utm e preferenze cookie espresse dall’utente.</p>
      </section>
      <section>
        <h2>Finalità</h2>
        <p>I dati sono usati per rispondere alle richieste degli utenti, monitorare l’uso del sito, misurare interazioni commerciali, migliorare i contenuti, predisporre futuri servizi digitali Viscar e amministrare l’area riservata aziendale.</p>
      </section>
      <section>
        <h2>Strumenti utilizzati</h2>
        <p>Il sito è predisposto per Google Analytics 4, Google Search Console, tracciamento eventi interno, misurazione delle richieste operative e gestione preferenze cookie con consenso selettivo.</p>
      </section>
      <section>
        <h2>Base giuridica e conservazione</h2>
        <p>I dati sono trattati sulla base del consenso dell’utente per gli analytics e dell’interesse legittimo o della necessità di dare seguito alla richiesta per i contatti operativi. I tempi di conservazione vanno definiti e validati con il consulente privacy dell’azienda.</p>
      </section>
      <section>
        <h2>Comunicazione dei dati</h2>
        <p>I dati possono essere trattati da fornitori tecnici che supportano il funzionamento del sito, dell’hosting, degli analytics e degli strumenti di amministrazione, nel rispetto delle misure tecniche e organizzative adottate.</p>
      </section>
      <section>
        <h2>Diritti dell’utente</h2>
        <p>L’utente può chiedere accesso, rettifica, limitazione o cancellazione dei dati e può modificare in ogni momento le preferenze cookie dal sito.</p>
      </section>
    </div>
  </main>
);

export const CookiePolicyPage = () => (
  <main data-testid="cookie-policy-page" className="legal-page">
    <div className="legal-page__shell">
      <span className="section-eyebrow">Cookie Policy</span>
      <h1>Cookie Policy Viscar</h1>
      <p>Questa è una bozza tecnica iniziale predisposta per il sito Viscar, da validare poi con il consulente legale/privacy dell’azienda.</p>
      <section>
        <h2>Cookie necessari</h2>
        <p>Servono al corretto funzionamento del sito, dell’area riservata, della sicurezza, della gestione delle sessioni e delle preferenze espresse dall’utente.</p>
      </section>
      <section>
        <h2>Cookie analytics</h2>
        <p>Con consenso attivo, Viscar può usare strumenti analytics per misurare visite, pagine viste, click su telefono, click WhatsApp, provenienza traffico e interazioni con il sito.</p>
      </section>
      <section>
        <h2>Strumenti attualmente predisposti</h2>
        <p>Il sito è predisposto per Google Analytics 4, Search Console, tracking eventi interno e dashboard amministrativa per il monitoraggio delle interazioni e delle richieste ricevute.</p>
      </section>
      <section>
        <h2>Gestione preferenze</h2>
        <p>Le preferenze cookie possono essere modificate in ogni momento tramite il link “Preferenze cookie” presente nel footer del sito.</p>
      </section>
    </div>
  </main>
);